/* eslint-disable max-lines */
import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from "react"
import { AppProps } from "next/app"
import type { NextPage } from "next"
import { ThemeProvider } from "next-themes"
import GlobalStylesComponent, { BonsaiTheme } from "../components/globalStyles"
import GDPRBanner from "../components/gdprBanner"
import "whatwg-fetch"
import { InMemoryCache, createHttpLink, ApolloLink, ApolloClient, ApolloProvider } from "@apollo/client"
import { RetryLink } from "@apollo/client/link/retry"
import { onError } from "@apollo/client/link/error"
import { DevelopmentErrorBoundary } from "../utils/error"
import setupLogRocketReact from "logrocket-react"
import LogRocket from "logrocket"
import { loadStripe, Stripe } from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"
import { AuthContext, Web3Context, RedirectContext, RedirectData, CookiesEnum } from "../utils/context"
import Bugsnag from "@bugsnag/js"
import BugsnagPluginReact, { BugsnagErrorBoundary } from "@bugsnag/plugin-react"
import { useEffectAsync } from "../utils/hooks/useEffectAsync"
import { GET_APP_PREFLIGHT_DATA } from "../graphql/queries/user.graphql"
import { StripeElementOptions } from "../components/paymentGate/mocks"
import { ErrorHandler } from "../components/errorHandler"
import NewAppAvailable from "../components/newAppAvailable"
import { appWithTranslation } from "next-i18next"
import querystring from "query-string"
import { ProtectedRoute } from "../hocs/protectedRoute"
import { Layout } from "../layouts/layout"
import cookies from "js-cookie"
import { Loading } from "../components/loading"
import mixpanel from "mixpanel-browser"
import { Routes } from "../routes"
const nextI18NextConfig = require("../../next-i18next.config.js")
import MetaTags from "../components/metaTags"

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
  theme: string
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

if (
  process.env.NEXT_PUBLIC_ENV == "production" &&
  typeof window !== "undefined" &&
  !document.location.pathname.startsWith("/healthcheck")
) {
  LogRocket.init("nvzmic/bonsai")
  setupLogRocketReact(LogRocket)
} else {
}
const blockchainNetworkID = process.env.NEXT_PUBLIC_NETWORK_ID
const stripeKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "pk_test_nq0KkznoE140wKVaz8pFdnxk00d8bNce3l"
const stripePromise: Promise<Stripe | null> = loadStripe(stripeKey, {
  apiVersion: "2020-03-02",
}).catch(() => {
  return loadStripe(stripeKey, {
    apiVersion: "2020-03-02",
  })
})

function App({ Component, pageProps, router }: AppPropsWithLayout) {
  const [rerender, setRerender] = useState(false)
  const triggerRerender = () => {
    if (rerender) {
      setRerender(false)
    } else {
      setRerender(true)
    }
    setPreflightMutationCalled(false)
    setPreflightDataLoaded(false)
  }
  const [loading, setLoading] = useState(true) // we set this to true by default, and set it to false once the application has mounted
  const [token, setToken] = useState(cookies.get(CookiesEnum.Token))
  const [userWallet, setUserWallet] = useState<string>("")
  const [web3, setWeb3] = useState<any>({})
  const [networkId] = useState<number>(Number(blockchainNetworkID))
  const [pageTheme, setPageTheme] = useState<string>(BonsaiTheme.Light)
  const [preflightMutationCalled, setPreflightMutationCalled] = useState(false)
  const [closeModal, setCloseModal] = useState(false)
  const [preflightData, setPreflightData] = useState<any>(null)
  const [preflightDataLoaded, setPreflightDataLoaded] = useState(false)
  const location = router.asPath
  const [userId, setUserId] = useState<string | undefined>(undefined)
  const [hasNewApp, setHasNewApp] = useState(false)
  const [referredByUser, setReferredByUser] = useState("")
  let ErrorBoundary: DevelopmentErrorBoundary | BugsnagErrorBoundary | undefined = DevelopmentErrorBoundary
  const [handleError, setHandleError] = useState<boolean>(false)
  const [micEnabled, setMicEnabled] = useState(true)
  const [cameraEnabled, setCameraEnabled] = useState(true)

  const [_data, _setData] = useState<RedirectData>({ data: {} })
  const setData = async (data: Record<string, any>, destination?: string): Promise<void> => {
    _setData({ destination: destination ?? "", data })
    destination && (await router.push(destination))
  }
  const getData = (destination?: string) => {
    if (_data.destination == destination) {
      return { destination: _data.destination, data: _data.data }
    }
    return { destination: "", data: _data.data }
  }

  const GDPR = () => {
    const cookieConsent = cookies.get("CookieConsent")
    if (preflightDataLoaded && !!preflightData && !!preflightData.data.Preflight) {
      if (preflightData.data.Preflight.success || cookieConsent) {
        return <></>
      } else {
        return <GDPRBanner />
      }
    }
  }

  useEffect(() => {
    // capturing query params in state variables
    const parsed = querystring.parse(window.location.search)
    if (parsed.u && !referredByUser) {
      setReferredByUser(parsed.u as string)
    }
  })

  if (
    process.env.NEXT_PUBLIC_ENV === "production" &&
    typeof window !== undefined &&
    typeof navigator !== "undefined" &&
    !document.location.pathname.startsWith("/healthcheck")
  ) {
    mixpanel.init("e3a83e232e9cef9ce3c7ca8aade18e22")
  }

  useEffect(() => {
    /* this has been introduced to address an issue we encountered when migrating from a react SPA to a SSR'd next application.*/
    setLoading(false)
  }, [])

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENV === "production" && typeof window !== "undefined") {
      Bugsnag.start({
        apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || "8c2204a734768055f35ee827eb4ae4a9",
        plugins: [new BugsnagPluginReact()],
        appType: "client",
        enabledReleaseStages: ["production"],
        releaseStage: process.env.NEXT_PUBLIC_ENV,
        appVersion: process.env.NEXT_PUBLIC_CODE_VERSION,
      })
      ErrorBoundary = Bugsnag.getPlugin("react")?.createErrorBoundary(React)
    }
  }, [])
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENV === "production" && typeof window !== "undefined") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      if (typeof fbq !== "undefined") {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        fbq("track", "PageView")
      }
    }
    window.scrollTo(0, 0)
    window.addEventListener("newAppAvailable", () => setHasNewApp(true))
  }, [location])

  const logoutLink = onError(({ graphQLErrors }) => {
    for (const error of graphQLErrors || []) {
      if (
        error.message.includes("User not logged in") ||
        error.message.includes("jwt malformed") ||
        error.message.includes("Malformed JWT")
      ) {
        cookies.remove(CookiesEnum.Token)
        setToken(undefined)
        setUserWallet("")
      }
      if (error.message.includes("Not authorised") && !token) {
        void router.push({ pathname: Routes.Login, query: { next: router.pathname } })
      }
    }
  })
  const apolloLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      headers: {
        authorization: "Bearer " + token,
      },
    })
    return forward(operation)
  })

  const serverUrl = process.env.NEXT_PUBLIC_API_URL || ""
  const stage = process.env.NEXT_PUBLIC_ENV == "development" ? "/development" : ""

  // we either talk to :
  // /development/graphql on playground or staging
  // /production/graphql on production
  const httpLink = createHttpLink({
    uri: `${serverUrl}${stage}/graphql`,
    fetch: fetch,
  })
  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 3000,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: (error) => !!error,
    },
  })
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    let swallowError = false
    if (graphQLErrors) {
      for (const error of graphQLErrors) {
        if (error.message?.includes("failed to fetch")) {
          swallowError = true
        } else {
          if (!!error.originalError) {
            Bugsnag.notify(error.originalError, function (event) {
              event.severity = "error"
            })
          } else {
            Bugsnag.notify(new Error(error.message), function (event) {
              event.severity = "error"
            })
          }
        }
      }
    }
    if (!!networkError) {
      swallowError = true
    }
    if (!swallowError) {
      setHandleError(true)
    }
  })
  const client = useMemo(() => {
    setPreflightMutationCalled(false)
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: apolloLink.concat(errorLink).concat(logoutLink).concat(retryLink).concat(httpLink),
    })
  }, [token])

  let isMounted = true
  useEffectAsync(
    async () => {
      if (!preflightMutationCalled) {
        const response = await client.query({
          query: GET_APP_PREFLIGHT_DATA,
          fetchPolicy: "no-cache",
          pollInterval: 0,
        })
        if (isMounted) {
          setPreflightMutationCalled(true)
          setPreflightData(response)
        }
      }
    },
    [preflightMutationCalled, preflightDataLoaded, preflightData, rerender],
    () => {
      isMounted = false
    },
  )
  if (!!preflightData && !preflightDataLoaded) {
    const { name, email, id } = preflightData.data.Preflight
    if (!!id) {
      if (
        process.env.NEXT_PUBLIC_ENV === "production" &&
        typeof window !== "undefined" &&
        typeof navigator !== "undefined"
      ) {
        try {
          LogRocket.identify(id as string, { name, email: email as unknown as string })
          mixpanel.register({
            Email: email,
            ID: id,
            Name: name,
            "Referring User": referredByUser,
          })
        } catch (e) {}
      }
      setUserId(id)
    } else {
      // we fall into this else when the preflight payload is empty; if the user has a token set, make sure to unset it
      setToken(undefined)
      setUserWallet("")
      setUserId(undefined)
    }
    setPreflightDataLoaded(true)
  }

  const getLayout =
    Component.getLayout ??
    ((page) => {
      return Layout(page)
    })

  useEffect(() => {
    setPageTheme(Component.theme)
  }, [Component.theme])

  return (
    <>
      <GlobalStylesComponent {...pageProps} />
      <MetaTags {...pageProps.metadata} />
      <ErrorBoundary>
        <ApolloProvider client={client}>
          <Elements stripe={stripePromise} options={StripeElementOptions}>
            <RedirectContext.Provider
              value={{
                getData,
                setData,
                _data,
                _setData,
              }}
            >
              <AuthContext.Provider
                value={{
                  triggerRerender,
                  closeModal,
                  setCloseModal,
                  micEnabled,
                  cameraEnabled,
                  setMicEnabled,
                  setCameraEnabled,
                  userId,
                  setUserId,
                  referredByUser,
                  setReferredByUser,
                  token,
                  setToken,
                  preflightData,
                  preflightDataLoaded,
                  setPreflightData,
                  setPreflightDataLoaded,
                  setPreflightMutationCalled,
                  pageTheme,
                }}
              >
                <Web3Context.Provider
                  value={{
                    userWallet,
                    setUserWallet,
                    web3,
                    setWeb3,
                    networkId,
                  }}
                >
                  {getLayout(
                    <ProtectedRoute router={router} token={token}>
                      {!loading ? (
                        <ThemeProvider
                          defaultTheme={BonsaiTheme.Light}
                          forcedTheme={pageTheme}
                          enableSystem={false}
                          enableColorScheme={false}
                        >
                          <Component {...pageProps} />
                          <ErrorHandler {...{ handleError }} />
                          {hasNewApp && <NewAppAvailable />}
                        </ThemeProvider>
                      ) : (
                        <Loading />
                      )}
                    </ProtectedRoute>,
                  )}
                  {GDPR()}
                </Web3Context.Provider>
              </AuthContext.Provider>
            </RedirectContext.Provider>
          </Elements>
        </ApolloProvider>
      </ErrorBoundary>
    </>
  )
}

export default appWithTranslation(App, nextI18NextConfig)
